import type { Ssm5Session } from '@packages/domain/ssm5';
import type { ReactNode } from 'react';

import { colors } from '@packages/core/styles';
import { LoadingCircle } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';

import type { Ssm5FreezeFramesTarget } from '../helpers/ssm5-paths';
import type { Ssm5FreezeFramesState } from '../hooks/use-ssm5-freeze-frames';
import type { Ssm5FreezeFramesFailureProps } from './ssm5-freeze-frames-failure';

import { freezeFrameEventDetail } from '../helpers/freeze-frame-event';
import { useSsm5FreezeFrames } from '../hooks/use-ssm5-freeze-frames';
import { ssm5ControlStyles } from '../ssm5.styles';
import { Ssm5FreezeFramesFailure } from './ssm5-freeze-frames-failure';
import { Ssm5FreezeFramesHeader } from './ssm5-freeze-frames-header';
import { Ssm5FreezeFramesReadout } from './ssm5-freeze-frames-readout';

/** Props for {@link Ssm5FreezeFramesPage}. */
export type Ssm5FreezeFramesPageProps = {
    /** Whether a failed read can be retried through the open connection. */
    canRetry: boolean;
    /** Returns to the event detail. */
    onReturnToDtcs: () => void;
    /** The session the page reads through. */
    session: Ssm5Session;
    /** The DTC whose freeze frame data is shown. */
    target: Ssm5FreezeFramesTarget;
};

const useStyles = createUseStyles({
    body: { display: 'flex', flex: 1, minHeight: 0, minWidth: 0 },
    page: {
        backgroundColor: colors.white,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        minWidth: 0,
        width: '100%',
    },
});

/** The actions available while a read has failed. */
type FailureActions = Pick<Ssm5FreezeFramesFailureProps, 'canRetry' | 'onRetry' | 'onReturnToDtcs'>;

/**
 * The full freeze frame page, read through the existing session.
 *
 * @param props - {@link Ssm5FreezeFramesPageProps}
 * @returns the header and the current read state
 */
export function Ssm5FreezeFramesPage({ canRetry, onReturnToDtcs, session, target }: Ssm5FreezeFramesPageProps) {
    const classes = useStyles();
    const { retry, state } = useSsm5FreezeFrames(session.cloudConnectorId, target);
    const eventDetail = state.status === 'loaded' ? freezeFrameEventDetail(state.readout) : undefined;
    const actions: FailureActions = { canRetry, onRetry: retry, onReturnToDtcs };

    return (
        <div className={classes.page}>
            <Ssm5FreezeFramesHeader
                dtcCode={target.dtcCode}
                eventDetail={eventDetail}
                target={target}
                vin={session.vin}
            />
            <div className={classes.body}>{pageBody(state, actions, session.cloudConnectorId)}</div>
        </div>
    );
}

/**
 * Select the body exhaustively from the read state.
 *
 * @param state - the current read state
 * @param actions - the available failure actions
 * @param cloudConnectorId - the Cloud Connector ID of the session
 * @returns the state body
 */
function pageBody(state: Ssm5FreezeFramesState, actions: FailureActions, cloudConnectorId: string): ReactNode {
    switch (state.status) {
        case 'failed':
            return <Ssm5FreezeFramesFailure isGone={state.isGone} {...actions} />;
        case 'loaded':
            return <Ssm5FreezeFramesReadout cloudConnectorId={cloudConnectorId} readout={state.readout} />;
        case 'loading':
            return <LoadingCircle style={ssm5ControlStyles.freezeFramesLoader} />;
    }
}
